import {getLocalePrefixes} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {parseUrl, urlInLanguage} from "@khan/urls";

import type {ProfileModelType} from "#shared-package/profile-model.ts";

// We only allow redirects to the locales that appear in the language dropdown
// (all of which are at the TEST, LIVE, or ROCK_STAR levels, though not all
// such locales appear because their sites might not be ready for public
// consumption). However, we won't suggest redirects to English.
// TODO: Remove this once CP-4389 has been completed.
export const REDIRECTABLE_PREFERRED_LOCALES = [
    // sync-start:language-picker-locales 2069270614 https://github.com/Khan/webapp/blob/master/pkg/services_shared/intl/ka_locales.go
    // TEST
    "as",
    "az",
    "cs",
    "da",
    "el",
    "gu",
    "hu",
    "id",
    "it",
    "ja",
    "lt",
    "kk",
    "kn",
    "ky",
    "lv",
    "mn",
    "mr",
    "my",
    "nl",
    "pt-pt",
    "ru",
    "sgn-us",
    "sv",
    "ta",
    "uz",

    // LIVE / ROCK_STAR
    "ar",
    "bg",
    "bn",
    "de",
    // English is intentionally excluded
    "es",
    "fr",
    "hi",
    "hy",
    "ka",
    "km",
    "ko",
    "nb",
    "or",
    "pa",
    "pl",
    "pt",
    "ro",
    "sr",
    "te",
    "tr",
    "uk",
    "ur",
    "vi",
    "zh-hans",
    // sync-end:language-picker-locales
] as const;

/**
 * Figure out the URL to direct the user to, to either see content in their
 * language (if logged out) or set their language preferences (if logged-in).
 *
 * @param {?ProfileModelType} profile the user's profile, if it exists
 * @param {string} preferredLocale the preferred locale that the user wishes
 *   to view the site with.
 * @param {string} href the location that you want to redirect the user to
 *   in their locale.
 */
export const getRedirectUrl = (
    profile: ProfileModelType | null | undefined,
    preferredLocale: string,
    href: string,
): string => {
    if (profile) {
        return `/settings/account?lang=${preferredLocale}`;
    }
    return urlInLanguage(preferredLocale, parseUrl(href));
};

/**
 * Get the user's preferred locale.
 *
 * This gets the user's preferred locale by looking at what the browser suggests
 * as their preferred locale. Returns an empty string if no preferred locale is
 * found or if we're already showing the user their preferred locale.
 *
 * @throws {KAError} if called server-side
 * @returns The user's preferred locale, if it can be determined; otherwise,
 *  an empty string.
 */
export const getPreferredLocale = (): string => {
    // Get the user's preferred language from the browser. Should be in the
    // format "en-US" per:
    // https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/language
    const browserLang = navigator.language.toLowerCase();

    // We permute out all the ways in which the locale can be represented, from
    // most to least specific.
    const localePrefixes = getLocalePrefixes(browserLang);

    for (const localePrefix of localePrefixes) {
        // Check to see if this is a locale that can be redirected to and that
        // we're not already showing the user their preferred locale.
        if (
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"zh-hans" | "id" | "tr" | "it" | "as" | "az" | "cs" | "da" | "el" | "gu" | "hu" | "ja" | "lt" | "kk" | "kn" | "ky" | "lv" | "mn" | "mr" | "my" | "nl" | "pt-pt" | "ru" | "sgn-us" | "sv" | ... 18 more ... | "vi"'.
            REDIRECTABLE_PREFERRED_LOCALES.includes(localePrefix) &&
            RequestInfo.kaLocale !== localePrefix
        ) {
            return localePrefix;
        }
    }

    // No match was found, so the user has no preferred language that we support
    // or we're already showing them their preferred locale.
    return "";
};
